/**
 * Operator Store
 *
 * Manages operator configuration and theme data fetched from
 * `/api/operators/current` on app boot.
 */

import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { operatorApi } from '../api/operator-api';
import type { Operator, Theme } from '../types/operator.types';

interface OperatorState {
  // State
  operator: Operator | null;
  theme: Theme | null;
  isInitialized: boolean;
  error: Error | null;

  // Actions
  initialize: () => Promise<void>;
  setOperator: (operator: Operator) => void;
  setTheme: (theme: Theme) => void;
  clearError: () => void;
  reset: () => void;
}

export const useOperatorStore = create<OperatorState>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        operator: null,
        theme: null,
        isInitialized: false,
        error: null,

        // Initialize operator and theme on app startup
        initialize: async () => {
          const { isInitialized } = get();

          // Prevent multiple initializations
          if (isInitialized) {
            return;
          }

          try {
            set({ error: null });

            // Fetch operator and theme from API
            const response = await operatorApi.getCurrent();

            // Extract operator and theme from response.data
            const operator = response.data;
            const theme = operator.defaultTheme;

            set({
              operator,
              theme,
              isInitialized: true,
            });

            // Apply theme colors and logo
            if (theme) {
              applyTheme(theme, operator);
            }
          } catch (error) {
            // Gracefully handle initialization failure
            console.error('[Operator] Failed to initialize:', error);

            // Set default operator (Monta) on failure - no theme applied
            set({
              operator: null,
              theme: null,
              isInitialized: true,
              error:
                error instanceof Error
                  ? error
                  : new Error('Failed to fetch operator configuration'),
            });
          }
        },

        // Set operator
        setOperator: (operator: Operator) => {
          set({ operator });
        },

        // Set theme
        setTheme: (theme: Theme) => {
          set({ theme });
          const { operator } = get();
          if (theme && operator) {
            applyTheme(theme, operator);
          }
        },

        // Clear error
        clearError: () => {
          set({ error: null });
        },

        // Reset to defaults
        reset: () => {
          set({
            operator: null,
            theme: null,
            isInitialized: false,
            error: null,
          });
        },
      })),
      {
        name: 'monta-operator',
        partialize: (state) => ({
          operator: state.operator,
          theme: state.theme,
        }),
        // Reuse cached branding only if it's for the operator this URL asks
        // for, so the app can render before /operators/current returns without
        // flashing another operator's theme.
        merge: (persisted, current) => {
          const cached = persisted as Partial<OperatorState> | undefined;
          const requested =
            new URLSearchParams(window.location.search).get('operator') ||
            'monta';
          if (!cached?.operator || cached.operator.identifier !== requested) {
            return current;
          }
          if (cached.theme) {
            applyTheme(cached.theme, cached.operator);
          }
          return { ...current, ...cached };
        },
      }
    ),
    { name: 'OperatorStore' }
  )
);

/**
 * Apply operator theme to the page
 * Sets CSS variables for colors, favicon, and metadata
 */
function applyTheme(theme: Theme, operator: Operator): void {
  if (typeof window === 'undefined') return;

  const root = document.documentElement;

  // Apply primary color as CSS variable
  if (theme.primary_color) {
    root.style.setProperty('--operator-primary-color', theme.primary_color);
  }

  // Apply secondary color as CSS variable
  if (theme.secondary_color) {
    root.style.setProperty('--operator-secondary-color', theme.secondary_color);
  }

  // Apply favicon if available
  if (theme.portal_fav_icon_path) {
    setFavicon(theme.portal_fav_icon_path);
  }

  // Update page metadata with operator name
  if (operator.identifier && operator.identifier !== 'monta') {
    updateMetadata(operator.identifier);
  }
}

/**
 * Set the page favicon dynamically
 */
function setFavicon(iconUrl: string): void {
  if (typeof document === 'undefined') return;

  // Remove existing favicon links
  const existingLinks = document.querySelectorAll(
    'link[rel="icon"], link[rel="shortcut icon"]'
  );
  existingLinks.forEach((link) => link.remove());

  // Create new favicon link
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/png';
  link.href = iconUrl;
  document.head.appendChild(link);
}

/**
 * Update page metadata based on operator
 */
function updateMetadata(operatorName: string): void {
  if (typeof document === 'undefined') return;

  // Capitalize first character of operator name
  const capitalizedName =
    operatorName.charAt(0).toUpperCase() + operatorName.slice(1);

  // Update page title
  document.title = `${capitalizedName} - Find EV Charging Stations`;

  // Update meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute(
      'content',
      `Find and explore ${capitalizedName} EV charging stations on the interactive map. View real-time availability, pricing, and charge speeds.`
    );
  }

  // Update Open Graph title
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) {
    ogTitle.setAttribute(
      'content',
      `${capitalizedName} - Find EV Charging Stations`
    );
  }

  // Update Open Graph description
  const ogDescription = document.querySelector(
    'meta[property="og:description"]'
  );
  if (ogDescription) {
    ogDescription.setAttribute(
      'content',
      `Find and explore ${capitalizedName} EV charging stations on the interactive map. View real-time availability, pricing, and charge speeds.`
    );
  }

  // Update Twitter title
  const twitterTitle = document.querySelector('meta[property="twitter:title"]');
  if (twitterTitle) {
    twitterTitle.setAttribute(
      'content',
      `${capitalizedName} - Find EV Charging Stations`
    );
  }

  // Update Twitter description
  const twitterDescription = document.querySelector(
    'meta[property="twitter:description"]'
  );
  if (twitterDescription) {
    twitterDescription.setAttribute(
      'content',
      `Find and explore ${capitalizedName} EV charging stations on the interactive map. View real-time availability, pricing, and charge speeds.`
    );
  }
}
